/**
 * TMobi Validador — app de portaria.
 *
 * Sobe o banco local antes de qualquer tela: o app precisa abrir e funcionar
 * sem rede, com a base que já baixou. O tema escolhido pelo operador é lido
 * junto, para o app não abrir escuro e piscar para claro no meio do sol.
 *
 * No computador as telas ficam numa coluna no meio da janela: o layout é o do
 * celular, e esticado a 1.366 px o teclado do login vira três teclas de 400 px.
 * Desfecho e conferência continuam ocupando a janela inteira — são modais, e
 * é assim que se enxergam do outro lado do balcão.
 *
 * O banco só aceita uma aba por vez. Aba que chega com ele ocupado oferece
 * "usar aqui"; aba que o perde para outra desmonta as telas (o que para
 * câmera, leitor e sync) e oferece o mesmo botão para pegá-lo de volta.
 */

import React, {useCallback, useEffect, useState} from 'react';
import {
  ActivityIndicator,
  Pressable,
  StatusBar,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import {NavigationContainer} from '@react-navigation/native';
import {SafeAreaProvider} from 'react-native-safe-area-context';

import {caixaAberto} from './src/caixa';
import {
  assumirBanco,
  BancoEmOutraAba,
  prepararBanco,
  quandoPerderPosse,
} from './src/dados/banco';
import {Navegacao, type ParamsPilha} from './src/navegacao';
import {carregarSessao} from './src/sessao';
import {TelaOutraAba} from './src/telas/OutraAba';
import {AvisoAtualizacao} from './src/ui/AvisoAtualizacao';
import {txt, useIdioma} from './src/ui/idioma';
import {carregarModo} from './src/ui/modo';
import {estilos, fontes, LARGURA_DA_COLUNA, useTema} from './src/ui/tema';

/** Daqui para cima é computador (ou tablet deitado): as telas viram coluna. */
const LARGURA_EM_COLUNA = 720;

/**
 * A falha de abertura, em português de portaria.
 *
 * O worker devolve um código porque ele roda fora da janela e não alcança o
 * idioma (ver banco.worker.ts). `SEM_ARMAZENAMENTO` é o caso em que o próprio
 * `navigator.storage.getDirectory()` falhou: o navegador não está liberando
 * armazenamento para o site. Não há o que o app tente — não existe permissão
 * de OPFS para pedir, é ajuste do aparelho, e é isso que a tela precisa dizer
 * em vez da frase do WebKit.
 */
function explicar(falha: string): {texto: string; detalhe: string} {
  // Causa conhecida: a frase basta, e o detalhe técnico sai. Ele existe para
  // quando não sabemos o porquê — aqui só competiria com a instrução.
  if (falha.startsWith('ANONIMA')) {
    return {
      texto: txt(
        'Em Navegação Privada o Safari não guarda dados do site.\nAbra este endereço numa aba normal.',
      ),
      detalhe: '',
    };
  }
  if (falha.startsWith('SEM_ARMAZENAMENTO')) {
    return {
      texto: txt(
        'O navegador não está guardando dados deste site, e sem isso o ' +
          'validador não abre. No iPhone: saia da Navegação Privada e, em ' +
          'Ajustes › Safari, desligue "Bloquear todos os cookies". Confira ' +
          'também se resta espaço no aparelho.',
      ),
      detalhe: falha.slice('SEM_ARMAZENAMENTO'.length).replace(' — ', ' · ').trim(),
    };
  }
  return {texto: falha.replace('BANCO_NAO_ABRE ', ''), detalhe: ''};
}

export default function App() {
  const t = useTema();
  useIdioma();
  const {width: largura} = useWindowDimensions();
  const emColuna = largura >= LARGURA_EM_COLUNA;

  // O que fica fora do React — o fundo atrás da página ao rolar e a barra do
  // navegador no celular — acompanha o tema, em vez de ficar branco.
  useEffect(() => {
    document.body.style.backgroundColor = t.fundo;
    let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.content = t.fundo;
  }, [t.fundo]);

  const [pronto, setPronto] = useState(false);
  const [inicial, setInicial] = useState<keyof ParamsPilha>('Login');
  const [evento, setEvento] = useState<{eventoId: number; eventoNome: string} | undefined>();
  const [falha, setFalha] = useState<string | null>(null);
  /** O banco está com outra aba: 'ocupado' ao abrir, 'assumida' se foi tirado desta. */
  const [outraAba, setOutraAba] = useState<'ocupado' | 'assumida' | null>(null);

  const abrir = useCallback(async () => {
    setPronto(false);
    setFalha(null);
    setOutraAba(null);
    try {
      await prepararBanco();
      await carregarModo();

      // Sem sessão volta ao login; com sessão mas sem caixa aberto, o
      // operador abre o posto antes de ver evento.
      const sessao = await carregarSessao();
      if (!sessao) {
        setInicial('Login');
      } else {
        const caixa = await caixaAberto(sessao.usuarioId);

        if (!caixa) {
          setInicial('Caixa');
        } else if (caixa.eventoId !== undefined) {
          // Caixa já amarrado a um evento: o app volta para ele, sem passar
          // pela lista de eventos.
          setEvento({
            eventoId: caixa.eventoId,
            eventoNome: caixa.eventoNome ?? '',
          });
          setInicial('Setores');
        } else {
          setInicial('Eventos');
        }
      }
    } catch (e) {
      if (e instanceof BancoEmOutraAba) {
        setOutraAba('ocupado');
      } else {
        setFalha((e as Error).message);
      }
    } finally {
      setPronto(true);
    }
  }, []);

  useEffect(() => {
    abrir();
  }, [abrir]);

  useEffect(() => quandoPerderPosse(() => setOutraAba('assumida')), []);

  const usarAqui = useCallback(async () => {
    await assumirBanco();
    await abrir();
  }, [abrir]);

  const barra = t.modo === 'dia' ? 'dark-content' : 'light-content';

  if (outraAba) {
    return (
      <SafeAreaProvider>
        <StatusBar barStyle={barra} />
        <TelaOutraAba assumida={outraAba === 'assumida'} aoUsarAqui={usarAqui} />
      </SafeAreaProvider>
    );
  }

  if (!pronto || falha) {
    return (
      <View
        style={{
          flex: 1,
          backgroundColor: t.fundo,
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}>
        <StatusBar barStyle={barra} />
        {falha ? (
          <View style={{gap: 18, alignItems: 'center', maxWidth: 420}}>
            <Text
              style={[
                estilos.titulo,
                {color: t.erro, textAlign: 'center', fontSize: 22, lineHeight: 27},
              ]}>
              {txt('Não foi possível abrir o validador')}
            </Text>

            {/* O detalhe do navegador fica menor e apagado: quem lê é o
                suporte no telefone, não o operador com a fila andando. */}
            <Text
              style={[
                estilos.subtitulo,
                {color: t.textoMedio, textAlign: 'center', fontFamily: fontes.regular},
              ]}>
              {explicar(falha).texto}
            </Text>

            {explicar(falha).detalhe ? (
              <Text
                style={[
                  estilos.subtitulo,
                  {
                    color: t.textoApagado,
                    textAlign: 'center',
                    fontFamily: fontes.regular,
                    fontSize: 13,
                  },
                ]}>
                {explicar(falha).detalhe}
              </Text>
            ) : null}

            <Pressable
              onPress={abrir}
              style={[estilos.botao, {alignSelf: 'stretch', backgroundColor: t.primaria}]}>
              <Text style={[estilos.textoBotao, {color: t.tintaPrimaria}]}>
                {txt('Tentar de novo')}
              </Text>
            </Pressable>
          </View>
        ) : (
          <ActivityIndicator color={t.primaria} size="large" />
        )}
      </View>
    );
  }

  return (
    <SafeAreaProvider>
      <StatusBar barStyle={barra} />
      <View style={{flex: 1, backgroundColor: t.fundo, alignItems: 'center'}}>
        <View
          style={
            emColuna
              ? {
                  flex: 1,
                  width: '100%',
                  maxWidth: LARGURA_DA_COLUNA,
                  borderLeftWidth: 1,
                  borderRightWidth: 1,
                  borderColor: t.superficie,
                }
              : {flex: 1, width: '100%'}
          }>
          {/* Versão nova no servidor: o aviso fica acima de tudo, e quem
              decide a hora de trocar é o operador. */}
          <AvisoAtualizacao />

          {/* Título fixo: no navegador o React Navigation poria o nome da
              tela ("Login", "Setores") na aba e no alternador de apps, onde o
              operador procura "Validador". */}
          <NavigationContainer documentTitle={{formatter: () => 'Validador'}}>
            <Navegacao inicial={inicial} evento={evento} />
          </NavigationContainer>
        </View>
      </View>
    </SafeAreaProvider>
  );
}
